/**
 * Ludo Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → dice roll → move selection →
 * move execution → animation → turn management → win.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Responsive layout: board + controls.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { AISpeedSlider } from '../../components/AISpeedSlider';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { LudoEngine } from '../LudoEngine';
import { LudoAI } from '../LudoAI';
import { LudoBoard } from './LudoBoard';
import { LudoDice } from './LudoDice';
import { MoveHistory } from './MoveHistory';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { LudoErrorHandler } from '../services/LudoErrorHandler';
import { LudoStatisticsService } from '../services/LudoStatisticsService';
import { LudoGamePersistence } from '../services/LudoGamePersistence';
import { MultiplayerLudoService } from '../services/MultiplayerLudoService';
import { WebSocketService } from '../../services/WebSocketService';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { GameResult } from '../../services/multiplayer/GenericMultiplayerService';
import {
  LudoGameSettings,
  LudoGameState,
  LudoMove,
  MoveResult,
  PlayerColor,
  PlayerType,
  TokenIdentifier,
  LudoGameProps,
  PLAYER_COLORS,
} from '../types';
import './LudoGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';
type GamePhase = 'waiting' | 'rolling' | 'moving' | 'animating' | 'completed';

const LudoGameInner: React.FC<LudoGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<LudoGameSettings | null>(null);
  const [gameState, setGameState] = useState<LudoGameState | null>(null);
  const [diceValue, setDiceValue] = useState<number | null>(null);
  const [rolling, setRolling] = useState(false);
  const [gamePhase, setGamePhase] = useState<GamePhase>('waiting');
  const [highlightedTokens, setHighlightedTokens] = useState<TokenIdentifier[]>([]);
  const [lastMoveResult, setLastMoveResult] = useState<MoveResult | null>(null);
  const [aiSpeedDelay, setAiSpeedDelay] = useState(500);

  const handleAiSpeedChange = useCallback((delay: number) => {
    setAiSpeedDelay(delay);
    aiSpeedDelayRef.current = delay;
  }, []);

  // Multiplayer state
  const [multiplayerService] = useState<MultiplayerLudoService>(
    () => new MultiplayerLudoService(WebSocketService.getInstance())
  );
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [playerColor, setPlayerColor] = useState<PlayerColor | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');

  // Refs
  const engineRef = useRef<LudoEngine>(new LudoEngine());
  const aiRef = useRef<LudoAI | null>(null);
  const aiSpeedDelayRef = useRef(500);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const hasAttemptedJoin = useRef(false);
  const isCreatingRoom = useRef(false);

  // Services (lazy init)
  const errorHandlerRef = useRef<LudoErrorHandler | null>(null);
  const statsServiceRef = useRef<LudoStatisticsService | null>(null);
  const persistenceRef = useRef<LudoGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new LudoErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new LudoStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new LudoGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize Ludo services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // =========================================================================
  // Multiplayer event handlers setup
  // =========================================================================
  useEffect(() => {
    multiplayerService.onGameStarted(() => {
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    });

    multiplayerService.onMoveUpdate((move: LudoMove, gameState: any) => {
      if (gameState) {
        setGameState(gameState);
      }
    });

    multiplayerService.onGameOver((result: GameResult) => {
      const message = result.result === 'win'
        ? `🎉 ${result.winner} wins!`
        : 'Game over!';
      NotificationService.getInstance().sendInfoNotification(message);
    });

    multiplayerService.onPlayerDisconnected(() => {
      setOpponentConnected(false);
      NotificationService.getInstance().sendWarningNotification(
        'Opponent disconnected. Waiting for reconnection...'
      );
    });

    multiplayerService.onPlayerConnected(() => {
      setOpponentConnected(true);
      NotificationService.getInstance().sendSuccessNotification('Opponent reconnected!');
    });

    multiplayerService.onGameStateReceived((gameState: any) => {
      if (gameState) {
        setGameState(gameState);
      }
    });

    multiplayerService.onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });

    multiplayerService.onError((error: string) => {
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });

    multiplayerService.onDiceResult((result) => {
      setDiceValue(result.value);
    });

    multiplayerService.onMoveResult((result) => {
      setLastMoveResult(result);
    });
  }, [multiplayerService]);

  // =========================================================================
  // Deep link room code handling
  // =========================================================================
  useEffect(() => {
    if (initialRoomCode && !roomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;
      setView('playing');
      setRoomCode(initialRoomCode);
      setWaitingForOpponent(true);

      multiplayerService.joinRoom(initialRoomCode)
        .then(() => {
          const color = multiplayerService.getPlayerColorTyped();
          if (color) setPlayerColor(color);
          setWaitingForOpponent(false);
          setOpponentConnected(true);
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setView('mode-select');
          setWaitingForOpponent(false);
          setRoomCode(null);
        });
    }
  }, [initialRoomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: LudoGameSettings) => {
      // Handle multiplayer room creation
      if (newSettings.mode === 'multiplayer-realtime') {
        if (isCreatingRoom.current) return;
        isCreatingRoom.current = true;

        multiplayerService.createRoom()
          .then((room) => {
            setRoomCode(room.code);
            setWaitingForOpponent(true);
            const color = multiplayerService.getPlayerColorTyped();
            if (color) setPlayerColor(color);
            setSettings(newSettings);
            setView('playing');
            isCreatingRoom.current = false;
          })
          .catch((error: any) => {
            NotificationService.getInstance().sendErrorNotification(
              `Failed to create room: ${error.message || 'Unknown error'}`
            );
            isCreatingRoom.current = false;
          });
        return;
      }

      const engine = engineRef.current;
      const count = newSettings.playerCount;

      // Build player types array
      const playerTypes: PlayerType[] = [];
      if (newSettings.mode === 'singleplayer') {
        playerTypes.push('human');
        for (let i = 0; i < (newSettings.aiOpponentCount || 1); i++) {
          playerTypes.push('ai');
        }
      } else {
        for (let i = 0; i < count; i++) {
          playerTypes.push('human');
        }
      }

      engine.initializeGame(count, playerTypes, newSettings);

      setSettings(newSettings);
      setGameState(engine.getGameState());
      setDiceValue(null);
      setRolling(false);
      setGamePhase('waiting');
      setHighlightedTokens([]);
      setLastMoveResult(null);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('ludo');

      // Set up AI if single player
      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new LudoAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `Ludo ${newSettings.mode === 'singleplayer' ? 'single player' : 'multiplayer'} game started!`,
        duration: 2000,
      });
    },
    [addNotification, multiplayerService]
  );

  /** Join a private online game room */
  const handleJoinPrivateGame = useCallback(async (code: string) => {
    setRoomCode(code);
    setWaitingForOpponent(true);
    setView('playing');

    try {
      await multiplayerService.joinRoom(code);
      const color = multiplayerService.getPlayerColorTyped();
      if (color) setPlayerColor(color);
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    } catch (error: any) {
      NotificationService.getInstance().sendErrorNotification(
        `Unable to join room ${code}. ${error.message || 'Please check the room code.'}`
      );
      setView('mode-select');
      setWaitingForOpponent(false);
      setRoomCode(null);
    }
  }, [multiplayerService]);

  const handleCancelWaiting = useCallback(() => {
    multiplayerService.leaveGame();
    setWaitingForOpponent(false);
    setRoomCode(null);
    setView('mode-select');
    setSettings(null);
    setPlayerColor(null);
    isCreatingRoom.current = false;
  }, [multiplayerService]);

  const handleRollDice = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver() || rolling || gamePhase === 'animating' || gamePhase === 'moving') return;

    const state = engine.getGameState();
    const currentPlayer = state.currentTurn;
    const playerType = state.playerTypes[currentPlayer];

    // In single player, only allow human to roll manually
    if (settings?.mode === 'singleplayer' && playerType === 'ai') {
      return;
    }

    setRolling(true);
    setGamePhase('rolling');
    const value = engine.rollDice();

    // Show rolling animation, then show valid moves
    setTimeout(() => {
      setDiceValue(value);
      setRolling(false);

      // Get valid moves
      const validMoves = engine.getValidMoves(currentPlayer);

      if (validMoves.length === 0) {
        // No valid moves — auto-skip
        addNotification({
          type: NotificationType.INFO,
          title: 'No Valid Moves',
          message: `${currentPlayer} has no valid moves. Turn skipped.`,
          duration: 2000,
        });

        setTimeout(() => {
          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);
          setGamePhase('waiting');
          setHighlightedTokens([]);
          setDiceValue(null);

          autoSaveGame(newState);
          scheduleAITurn(newState);
        }, 800);
        return;
      }

      // Check for triple-six before showing moves
      if (engine.getConsecutiveSixes() >= 3) {
        // This shouldn't happen since we check in executeMove, but handle gracefully
        addNotification({
          type: NotificationType.WARNING,
          title: 'Triple Six!',
          message: `${currentPlayer} rolled three 6s in a row — turn forfeited!`,
          duration: 3000,
        });
        engine.advanceTurn();
        const newState = engine.getGameState();
        setGameState(newState);
        setGamePhase('waiting');
        setHighlightedTokens([]);
        setDiceValue(null);
        scheduleAITurn(newState);
        return;
      }

      // Highlight valid tokens
      const highlighted: TokenIdentifier[] = validMoves.map((m) => ({
        player: m.player,
        tokenIndex: m.tokenIndex,
      }));
      // Deduplicate
      const seen = new Set<string>();
      const unique = highlighted.filter((t) => {
        const key = `${t.player}-${t.tokenIndex}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setHighlightedTokens(unique);
      setGamePhase('moving');
      setGameState(engine.getGameState());
    }, 800);
  }, [rolling, gamePhase, settings, addNotification]);

  const handleTokenSelect = useCallback(
    (player: PlayerColor, tokenIndex: number) => {
      const engine = engineRef.current;
      if (engine.isGameOver() || gamePhase !== 'moving') return;

      const state = engine.getGameState();
      if (state.currentTurn !== player) return;

      const validMoves = engine.getValidMoves(player);
      const move = validMoves.find((m) => m.tokenIndex === tokenIndex);
      if (!move) return;

      executeMove(move);
    },
    [gamePhase]
  );

  const executeMove = useCallback(
    (move: LudoMove) => {
      const engine = engineRef.current;
      setGamePhase('animating');
      setHighlightedTokens([]);

      try {
        const result = engine.executeMove(move.player, move);
        setLastMoveResult(result);

        if (!result.success) {
          errorHandlerRef.current?.handleGameLogicError(result.error || 'Move failed');
          setGamePhase('waiting');
          return;
        }

        // Show notifications for special events
        if (result.captured) {
          addNotification({
            type: NotificationType.WARNING,
            title: '💥 Capture!',
            message: `${move.player} captured ${result.captured.player}'s token ${result.captured.tokenIndex + 1}!`,
            duration: 2500,
          });
        }

        if (result.reachedHome) {
          addNotification({
            type: NotificationType.SUCCESS,
            title: '🏠 Home!',
            message: `${move.player}'s token ${move.tokenIndex + 1} reached home!`,
            duration: 2500,
          });
        }

        if (result.tripleSix) {
          addNotification({
            type: NotificationType.WARNING,
            title: 'Triple Six!',
            message: `${move.player} rolled three 6s — turn forfeited!`,
            duration: 3000,
          });
        }

        if (result.extraTurn) {
          addNotification({
            type: NotificationType.INFO,
            title: '⚡ Extra Turn!',
            message: `${move.player} rolled a 6 — extra turn!`,
            duration: 2000,
          });
        }

        // After animation delay, advance turn
        setTimeout(() => {
          if (result.gameOver) {
            handleGameOver(result);
            return;
          }

          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);
          setGamePhase('waiting');
          setDiceValue(null);

          autoSaveGame(newState);
          scheduleAITurn(newState);
        }, 600);
      } catch (error) {
        setGamePhase('waiting');
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'Move execution failed'
        );
      }
    },
    [addNotification]
  );

  const scheduleAITurn = useCallback(
    (state: LudoGameState) => {
      if (!aiRef.current) return;
      const currentPlayer = state.currentTurn;
      const playerType = state.playerTypes[currentPlayer];
      if (playerType !== 'ai') return;
      if (state.gameStatus.type !== 'active') return;

      // Use user-controlled delay instead of AI's built-in delay
      setTimeout(() => {
        handleAITurn();
      }, aiSpeedDelayRef.current);
    },
    []
  );

  const handleAITurn = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver() || rolling) return;

    const state = engine.getGameState();
    const currentPlayer = state.currentTurn;

    // Roll dice
    setRolling(true);
    setGamePhase('rolling');
    const value = engine.rollDice();

    setTimeout(() => {
      setDiceValue(value);
      setRolling(false);

      const validMoves = engine.getValidMoves(currentPlayer);

      if (validMoves.length === 0) {
        // No valid moves — skip
        addNotification({
          type: NotificationType.INFO,
          title: 'AI Turn',
          message: `AI (${currentPlayer}) has no valid moves. Turn skipped.`,
          duration: 1500,
        });

        setTimeout(() => {
          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);
          setGamePhase('waiting');
          setDiceValue(null);

          autoSaveGame(newState);
          scheduleAITurn(newState);
        }, 600);
        return;
      }

      // AI selects move
      const aiMove = aiRef.current!.selectMove(engine, currentPlayer, value, validMoves);

      // Brief highlight then execute
      setHighlightedTokens([{ player: aiMove.player, tokenIndex: aiMove.tokenIndex }]);

      setTimeout(() => {
        executeMove(aiMove);
      }, 400);
    }, 500);
  }, [rolling, addNotification, executeMove, scheduleAITurn]);

  const autoSaveGame = useCallback(
    (state: LudoGameState) => {
      if (persistenceRef.current && settings) {
        persistenceRef.current.autoSave(settings.mode, state, {
          aiDifficulty: settings.aiDifficulty,
          startedAt: state.startedAt,
          lastSavedAt: Date.now(),
        });
      }
    },
    [settings]
  );

  const handleGameOver = useCallback(
    (result: MoveResult) => {
      const engine = engineRef.current;
      const state = engine.getGameState();
      setGameState(state);
      setGamePhase('completed');
      setView('game-over');

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current && settings && result.winner) {
        const capturesMade = state.moveHistory.filter((m) => m.captured).length;
        const sixesRolled = state.moveHistory.filter((m) => m.diceValue === 6).length;

        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: settings.mode,
            difficulty: settings.aiDifficulty,
            playerCount: state.playerCount,
            winner: result.winner,
            reason: 'all_tokens_home',
            totalMoves: state.moveHistory.length,
            capturesMade,
            sixesRolled,
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
          },
          // For single player, the human is always the first player (red)
          settings.mode === 'singleplayer' ? 'red' : result.winner,
          isAuthenticated ? user?.id : undefined
        );
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: '🎉 Game Over!',
        message: `${result.winner} wins the game!`,
        duration: 5000,
      });
    },
    [addNotification, settings, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    if (roomCode) {
      multiplayerService.leaveGame();
    }
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setDiceValue(null);
    setLastMoveResult(null);
    setSettings(null);
    setHighlightedTokens([]);
    setGamePhase('waiting');
    setRoomCode(null);
    setWaitingForOpponent(false);
    setOpponentConnected(false);
    setPlayerColor(null);
    setConnectionStatus('connected');
    isCreatingRoom.current = false;
  }, [roomCode, multiplayerService]);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // Trigger AI on initial turn if AI goes first
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    const engine = engineRef.current;
    const state = engine.getGameState();
    const currentPlayer = state.currentTurn;
    const playerType = state.playerTypes[currentPlayer];
    if (playerType === 'ai' && !engine.isGameOver() && !rolling && gamePhase === 'waiting') {
      setTimeout(() => handleAITurn(), aiSpeedDelayRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const ludoConfig: GameModeConfig = {
      gameName: 'Ludo',
      gameIcon: '🎯',
      subtitle: 'Classic board game',
      availableModes: ['ai', 'local', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
          playerCount: 2,
          aiOpponentCount: 1,
        });
      } else if (selection.mode === 'local') {
        handleStartGame({
          mode: 'local-multiplayer',
          playerCount: 2,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime', playerCount: 2 });
      }
    };

    return (
      <div className="ludo-game">
        <UniversalGameModeSelector
          config={ludoConfig}
          onStart={handleModeSelection}
          onJoinRoom={handleJoinPrivateGame}
          onCreateRoom={() => handleStartGame({ mode: 'multiplayer-realtime', playerCount: 2 })}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) {
    if (waitingForOpponent && roomCode) {
      return (
        <div className="ludo-game">
          <RoomCodeDisplay
            roomCode={roomCode}
            gameType="checkers"
            playerRole={playerColor || undefined}
            playerName="Guest"
            maxPlayers={4}
            onCancel={handleCancelWaiting}
            showCancelButton={true}
          />
        </div>
      );
    }
    return null;
  }

  const currentPlayer = gameState.currentTurn;
  const currentPlayerType = gameState.playerTypes[currentPlayer];
  const isHumanTurn = currentPlayerType === 'human';
  const isDiceDisabled = !isHumanTurn || rolling || gamePhase !== 'waiting' || gameState.gameStatus.type !== 'active';

  // Game stats for game-over screen
  const capturesMade = gameState.moveHistory.filter((m) => m.captured).length;
  const sixesRolled = gameState.moveHistory.filter((m) => m.diceValue === 6).length;

  // Game over config
  const gameOverConfig = (view === 'game-over' && gameState.gameStatus.type === 'win') ? {
    show: true,
    result: 'win' as 'win' | 'loss' | 'draw',
    title: '🎉 Game Over!',
    subtitle: `${gameState.gameStatus.winner} wins! (${gameState.moveHistory.length} moves, ${capturesMade} captures, ${sixesRolled} sixes)`,
    icon: '🎉',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Ludo',
    board: (
      <div className="ludo-game__board-area">
        <LudoBoard
          engine={engineRef.current}
          onTokenSelect={handleTokenSelect}
          highlightedTokens={highlightedTokens}
          currentTurn={currentPlayer}
          isInteractive={isHumanTurn && gamePhase === 'moving'}
          playerCount={gameState.playerCount}
          gamePhase={gamePhase}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'players',
        title: 'Players',
        icon: '👥',
        content: (
          <div className="ludo-game__player-info" role="status" aria-live="polite">
            {/* Player token counts */}
            <div className="ludo-game__positions">
              {gameState.activePlayers.map((color) => (
                <div
                  key={color}
                  className={`ludo-game__player-row ${color === currentPlayer ? 'ludo-game__player-row--active' : ''}`}
                >
                  <span className={`ludo-game__player-dot ludo-game__player-dot--${color}`} aria-hidden="true" />
                  <span className="ludo-game__player-name">
                    {gameState.playerTypes[color] === 'ai' ? `AI (${color})` : color}
                  </span>
                  <span className="ludo-game__player-home">
                    🏠 {gameState.tokensHome[color]}/4
                  </span>
                </div>
              ))}
            </div>

            {/* Extra turn warning */}
            {gameState.consecutiveSixes > 0 && (
              <div className="ludo-game__extra-turn-warning" role="alert">
                ⚠️ Consecutive 6s: {gameState.consecutiveSixes}/3
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'dice',
        title: 'Dice',
        icon: '🎲',
        content: (
          <LudoDice
            value={diceValue}
            rolling={rolling}
            disabled={isDiceDisabled}
            onRoll={handleRollDice}
            playerColor={currentPlayer}
          />
        ),
      },
      {
        id: 'history',
        title: 'Move History',
        icon: '📜',
        content: <MoveHistory moves={gameState.moveHistory} maxHeight={180} />,
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          <span className={`ludo-game__player-dot ludo-game__player-dot--${currentPlayer}`} aria-hidden="true" />
          {' '}
          {currentPlayerType === 'ai'
            ? `AI (${currentPlayer}) is thinking...`
            : `${currentPlayer}'s turn`}
          {gamePhase === 'moving' && isHumanTurn && (
            <span className="ludo-game__phase-hint"> — Select a token to move</span>
          )}
        </span>
      ),
      gameStatus: gameState.gameStatus.type === 'win' ? 'Game Over' : undefined,
      gameStatusType: gameState.gameStatus.type === 'win' ? 'ended' : 'active',
      aiThinking: currentPlayerType === 'ai',
      connectionStatus: settings?.mode === 'multiplayer-realtime' ? connectionStatus : undefined,
      roomCode: roomCode || undefined,
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    secondaryActions: settings?.mode === 'singleplayer' ? [
      <AISpeedSlider
        key="ai-speed"
        value={aiSpeedDelay}
        onChange={handleAiSpeedChange}
      />,
    ] : undefined,
    gameOver: gameOverConfig,
  };

  return (
    <div className="ludo-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const LudoGame: React.FC<LudoGameProps> = (props) => (
  <ErrorBoundary componentName="Ludo">
    <LudoGameInner {...props} />
  </ErrorBoundary>
);

export { LudoGame };
export default LudoGame;
